/**
 * Discord admin API client — Phase 14 Plan 14-03 Task 1.
 *
 * Thin fetch wrappers around Plan 14-02's `/api/cms/admin/integrations/discord/*`
 * super-admin endpoints. Auth rides the existing session cookie
 * (credentials: 'include') — the SuperAdminUser extractor on the backend
 * gates the four routes (GET/PUT/DELETE /config + POST /send).
 *
 * Error envelope contract from Plan 14-02 is `{ error, code, details }`.
 * Errors are surfaced as `Error` instances with a `.code` property attached
 * so UI code can pattern-match (e.g. `INTEGRATION_NOT_CONFIGURED` shows the
 * configure-first prompt; `DISCORD_RATE_LIMITED` surfaces retryAfterSeconds).
 *
 * Pattern mirrors `src/lib/gitlab/api.ts` (Plan 13-03) verbatim — same
 * fetch shape, same envelope decoding. The 503 + GITLAB_DISABLED ->
 * GitLabDisabledError class trick is NOT replicated here because the
 * Discord disabled state is per-row (no creds row exists) rather than
 * per-feature-flag (env var), and the page renders the configure form
 * inline rather than a banner.
 */

import { apiRequest } from '@/lib/api-client'

import {
  DiscordConfigSchema,
  type DiscordConfig,
  type SendDiscordBody,
} from './types'

const BASE = '/api/cms/admin/integrations/discord'
const FETCH_OPTS: RequestInit = { credentials: 'include' }

/**
 * Error class carrying the typed `code` + `details` from the Plan 14-02
 * error envelope. The page handler can `instanceof DiscordApiError` and
 * branch on `err.code` (e.g. show retry hint when `DISCORD_RATE_LIMITED`).
 */
export class DiscordApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly details: unknown
  constructor(
    message: string,
    code: string,
    status: number,
    details: unknown = null,
  ) {
    super(message)
    this.name = 'DiscordApiError'
    this.code = code
    this.status = status
    this.details = details
    // Restore prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, DiscordApiError.prototype)
  }
}

async function throwFromResponse(response: Response): Promise<never> {
  const data: { error?: string; code?: string; details?: unknown } =
    await response.json().catch(() => ({}))
  const message = data.error || `Request failed (${response.status})`
  const code = data.code || `HTTP_${response.status}`
  throw new DiscordApiError(message, code, response.status, data.details)
}

export const discordApi = {
  /**
   * GET /api/cms/admin/integrations/discord/config — returns the masked
   * connection state. `connected: false` + null fields when no row exists.
   */
  async getConfig(): Promise<DiscordConfig> {
    const res = await apiRequest(`${BASE}/config`, FETCH_OPTS)
    if (!res.ok) await throwFromResponse(res)
    return DiscordConfigSchema.parse(await res.json())
  },

  /**
   * PUT /api/cms/admin/integrations/discord/config — saves a new (or replaces
   * existing) Discord webhook URL. Server validates that the URL host is
   * discord.com / canary.discord.com / discordapp.com and that the path
   * starts with /api/webhooks/ before encrypting and storing.
   */
  async putConfig(body: { webhookUrl: string; label?: string }): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * DELETE /api/cms/admin/integrations/discord/config — hard-delete (no
   * soft-delete column per Plan 14-02 decision). To "undelete" the admin
   * just re-PUTs the same webhook URL.
   */
  async deleteConfig(): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'DELETE',
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * POST /api/cms/admin/integrations/discord/send — posts a message to the
   * configured webhook. Server enforces `content OR embeds`; the client
   * short-circuits the same check to avoid a doomed network round-trip
   * when the composer is empty.
   */
  async sendMessage(body: SendDiscordBody): Promise<void> {
    const hasContent = (body.content?.trim() ?? '').length > 0
    const hasEmbeds = (body.embeds?.length ?? 0) > 0
    if (!hasContent && !hasEmbeds) {
      throw new Error('content or at least one embed required')
    }
    const res = await apiRequest(`${BASE}/send`, {
      ...FETCH_OPTS,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwFromResponse(res)
  },
} as const

export type DiscordApi = typeof discordApi
